// =============================================================================
// io/diagnostics/errorLog.ts — the last few things that went wrong, kept.
// -----------------------------------------------------------------------------
// A ring buffer of recent errors, attached to a feedback report so triage does
// not have to start with "can you reproduce it?".
//
// ── 🚨 THIS IS THE PART OF A BUG REPORT THAT MAKES IT ACTIONABLE ──
//
// "The chart went blank" plus a stack trace is a fix. "The chart went blank" on
// its own is a conversation. The old client attaches the last ten and it is the
// single most useful field in the payload — and the easiest one to leave out,
// because everything works fine without it right up until someone reports
// something.
//
// ── 🚨 IT MUST NEVER BE THE THING THAT BREAKS THE APP ──
//
// It wraps `console.error`, `console.warn`, `window.onerror` and
// `unhandledrejection` — four global hooks, installed before React mounts, on
// every page load for every user. So:
//
//   • every capture is inside a try/catch that swallows. A logger that throws
//     while logging an error would turn one broken card into a broken page
//   • the original console method is ALWAYS called, first, whatever happens
//     here. Losing a developer's console output to a diagnostic feature would
//     be a bad trade
//   • installing twice is a no-op. React StrictMode double-invokes effects in
//     development, and a second wrap would log everything twice and stack a
//     wrapper on a wrapper
//
// ── ⚠ WHAT IT DELIBERATELY DOES NOT DO ──
//
// No network. No storage. It lives in memory for the length of the session and
// is read only when someone presses Send feedback. Nothing here phones home on
// its own, and a user who never sends feedback never transmits any of it.
// =============================================================================

/** One captured entry. Mirrors the shape the Edge Function stores. */
export interface LoggedError {
  /** Epoch millis. ⚠ Read from the clock at capture time — see below. */
  ts: number;
  kind: 'error' | 'warn';
  msg: string;
  stack: string | null;
  /** `file.ts:120:8`, when the browser gives one. */
  source: string | null;
}

/**
 * How many a FEEDBACK REPORT carries.
 *
 * ⚠ TEN, matching what the old client sends and what the function stores. More
 * would be truncated server-side.
 *
 * 🚨 THIS IS NOT THE BUFFER SIZE ANY MORE — see `LOG_MAX`. It was, and raising
 * it in place would have silently changed the size of every feedback payload
 * this client sends. Two consumers, two numbers, one buffer.
 */
export const ERROR_LOG_MAX = 10;

/**
 * How many are RETAINED for the activity log panel.
 *
 * ⚠ 200, matching the old client's `LOG_MAX`. Ten is far too few to review a
 * session with — a single failing card can emit three warnings, so the ten most
 * recent can easily all belong to one moment and hide everything before it.
 *
 * The cap still exists for the reason the old one did: a page left open all day
 * with a warning in a render loop must not grow without bound.
 */
export const LOG_MAX = 200;

let buffer: LoggedError[] = [];
let installed = false;

/**
 * ── ⚠ UNREAD COUNT AND SUBSCRIBERS: WHY AN io/ MODULE HAS THEM ──
 *
 * The badge has to change the moment something fails, and nothing re-renders
 * React when a module-level array is pushed to. The alternative — polling on a
 * timer — is worse: it burns a render a second forever to catch an event that
 * happens twice a week.
 *
 * ⚠ This stays React-free. It is a version counter and a listener set; `ui/`
 * adapts it with `useSyncExternalStore`. `io/` may not import React and does
 * not.
 */
let unread = 0;
let version = 0;
const listeners = new Set<() => void>();

function bump(): void {
  version++;
  // ⚠ Iterated over a COPY. A listener that unsubscribes itself while being
  // notified would otherwise mutate the set mid-iteration.
  for (const l of [...listeners]) {
    try { l(); } catch { /* a broken listener must not break logging */ }
  }
}

/** Longest single message kept. The function clips at 500 anyway. */
const MSG_MAX = 500;
const STACK_MAX = 2000;

function clip(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/**
 * Turn whatever was passed to `console.error(...)` into one string.
 *
 * ⚠ AN ERROR OBJECT LOGS AS `[object Object]` UNDER `String()`, which is the
 * single most useless thing this could record. Errors are unwrapped to their
 * message, and anything else is JSON where that works.
 */
function argToText(a: unknown): string {
  if (a instanceof Error) return a.message || a.name;
  if (typeof a === 'string') return a;
  if (a === null) return 'null';
  if (a === undefined) return 'undefined';
  try {
    return JSON.stringify(a) ?? String(a);
  } catch {
    // Circular structures, getters that throw, DOM nodes.
    return String(a);
  }
}

function firstStack(args: readonly unknown[]): string | null {
  for (const a of args) {
    if (a instanceof Error && a.stack) return clip(a.stack, STACK_MAX);
  }
  return null;
}

/**
 * Record one entry.
 *
 * @param now injected so tests do not depend on the wall clock (D26). The
 *   default reads it, because this is `io/` and a log entry without a time is
 *   not much of a log entry.
 */
export function record(
  entry: Omit<LoggedError, 'ts'>, now: number = Date.now(),
): void {
  try {
    buffer.push({
      ts: now,
      kind: entry.kind,
      msg: clip(entry.msg, MSG_MAX),
      stack: entry.stack ? clip(entry.stack, STACK_MAX) : null,
      source: entry.source ? clip(entry.source, 300) : null,
    });
    if (buffer.length > LOG_MAX) buffer = buffer.slice(-LOG_MAX);
    unread++;
    bump();
  } catch {
    // Never let logging break the thing it is logging about.
  }
}

/**
 * The last few, oldest first — what a FEEDBACK REPORT attaches.
 *
 * 🚨 CAPPED AT `ERROR_LOG_MAX`, NOT AT THE BUFFER LENGTH. It used to return the
 * whole buffer, which was the same thing while the buffer held ten. Now that the
 * log panel retains 200, returning `buffer.slice()` here would quietly grow
 * every feedback payload twentyfold and blow the server-side clip — a change to
 * a shipped API made by editing an unrelated constant.
 */
export function recentErrors(): LoggedError[] {
  return buffer.slice(-ERROR_LOG_MAX);
}

/**
 * ── 🚨 THE FUNNEL. Turn a caught error into the message the USER sees, and
 * record it on the way past. ──
 *
 * **Use this instead of `e instanceof Error ? e.message : String(e)` anywhere a
 * failure is about to be shown to someone.**
 *
 * ── WHY IT RETURNS THE MESSAGE INSTEAD OF JUST LOGGING ──
 *
 * Because a reporter you have to remember to call is a reporter that does not
 * get called. Measured on 2026-08-27: this client reported failures to the user
 * in about **99 places** and told the log in **one**. The old client reports in
 * 291 places and logs in all 291 — not through discipline, but because its
 * `toast()` shows the message and records it in the same call. You cannot tell
 * the user something failed there without recording it.
 *
 * ▶ This is that property in the shape this client actually uses: the value the
 * error state needs comes back OUT of the thing that logs. Skipping the log now
 * means having no message to display.
 *
 * ⚠ THE RETURNED STRING IS THE BARE MESSAGE, NOT THE SCOPED ONE. The scope goes
 * in the log, where "which read failed" is the whole question. On screen the
 * surrounding copy already says what failed — "Couldn't load your dashboard" —
 * and prefixing it would change two dozen screens' wording under cover of
 * adding logging.
 *
 * ── 🚨 A `fallback` CHANGES WHAT IS SHOWN, NEVER WHAT IS LOGGED ──
 *
 * Two callers substitute friendlier copy for a non-`Error` throw — "The coach
 * could not be reached." rather than whatever `String(e)` produces. That is
 * right for the screen and would be a disaster for the log: recording the
 * friendly sentence discards the only evidence of what was actually thrown, in
 * the one record that exists to preserve it.
 *
 * ▶ So the log always gets `String(e)`, and only the RETURNED string is
 * softened. The user reads the kind sentence; the log keeps the truth.
 *
 * @param scope what was being loaded, for the log line — `accounts`, `trades`.
 * @param fallback shown (not logged) when `e` is not an `Error`.
 */
export function reportFailure(scope: string, e: unknown, fallback?: string): string {
  const raw = e instanceof Error ? e.message : String(e);
  record({
    kind: 'error',
    // ⚠ `raw`, always. See above.
    msg: `${scope}: ${raw}`,
    stack: e instanceof Error ? (e.stack ?? null) : null,
    source: null,
  });
  return e instanceof Error ? e.message : (fallback ?? raw);
}

/**
 * The same funnel, for a caller that already knows how to describe its own
 * failure better than a generic reader could.
 *
 * ⚠ TWO CALLERS, AND BOTH EARNED IT. The snapshot capture rejects with a DOM
 * `Event`, where `String(e)` is `[object Event]` — worse than useless — and its
 * `rasterErrorText` names the element instead. Auth rejects with Supabase's own
 * shape, and `readableError` turns that into a sentence. In both cases the
 * bespoke description IS the best evidence available, so it is what gets logged.
 *
 * ▶ Prefer `reportFailure`. Reach for this only where a generic
 * `String(e)` would record nothing worth reading.
 */
export function reportFailureAs(scope: string, e: unknown, display: string): string {
  record({
    kind: 'error',
    msg: `${scope}: ${display}`,
    stack: e instanceof Error ? (e.stack ?? null) : null,
    source: null,
  });
  return display;
}

/** Everything retained, oldest first. A copy — callers must not mutate it. */
export function logEntries(): LoggedError[] {
  return buffer.slice();
}

/**
 * How many have arrived since the panel was last opened.
 *
 * ⚠ Counts BOTH kinds. A warning is how this client reports a partial failure —
 * a card that could not load, a row it declined to write — and a badge that only
 * counted hard errors would stay dark through exactly those.
 */
export function unreadLogCount(): number {
  return unread;
}

/** Called when the panel is opened. */
export function markLogRead(): void {
  if (unread === 0) return;
  unread = 0;
  bump();
}

/** Subscribe to any change. Returns the unsubscribe. */
export function subscribeLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * A version that changes on every mutation.
 *
 * ⚠ A NUMBER, deliberately, because this is what `useSyncExternalStore` reads.
 * Returning the array instead would hand React a fresh reference on every call
 * and spin it in an infinite re-render — the standard trap with that hook.
 */
export function logVersion(): number {
  return version;
}

/** Empties the log. Used by the panel's Clear, and by tests. */
export function clearErrorLog(): void {
  buffer = [];
  unread = 0;
  bump();
}

/**
 * Install the four global hooks. Safe to call more than once.
 *
 * @returns a function that removes them again, for tests.
 */
export function installErrorLog(target: Window & typeof globalThis = window): () => void {
  if (installed) return () => {};
  installed = true;

  const origError = target.console.error;
  const origWarn = target.console.warn;

  const wrap = (kind: 'error' | 'warn', orig: (...a: unknown[]) => void) =>
    (...args: unknown[]): void => {
      // 🚨 THE ORIGINAL FIRST, ALWAYS, AND OUTSIDE THE TRY. If capturing throws,
      // the developer still gets their console output — and if calling the
      // original threw, nothing this module does could help anyway.
      orig.apply(target.console, args);
      try {
        record({
          kind,
          msg: args.map(argToText).join(' '),
          stack: firstStack(args),
          source: null,
        });
      } catch { /* swallowed on purpose — see the header */ }
    };

  target.console.error = wrap('error', origError as (...a: unknown[]) => void);
  target.console.warn = wrap('warn', origWarn as (...a: unknown[]) => void);

  const onError = (e: ErrorEvent): void => {
    try {
      record({
        kind: 'error',
        msg: e.message || 'Uncaught error',
        stack: e.error instanceof Error ? (e.error.stack ?? null) : null,
        // ⚠ The line and column are what make a minified stack usable at all.
        source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : null,
      });
    } catch { /* swallowed */ }
  };

  const onRejection = (e: PromiseRejectionEvent): void => {
    try {
      const r: unknown = e.reason;
      record({
        kind: 'error',
        msg: `Unhandled promise rejection: ${argToText(r)}`,
        stack: r instanceof Error ? (r.stack ?? null) : null,
        source: null,
      });
    } catch { /* swallowed */ }
  };

  /**
   * ── 🚨 THE THIRD CHANNEL, AND NOTHING ELSE IN THIS FILE CAN SEE IT ──
   *
   * A resource blocked by our OWN content security policy is not an error and
   * not a rejection. The browser writes its message to the console itself
   * rather than routing it through `console.error`, so the wrapper above misses
   * it too — measured in the live page, which reported one
   * `securitypolicyviolation` event and an EMPTY `consoleErrorCaptured`.
   *
   * ▶ So a font, an image, or a `connect-src` host we blocked by accident is
   * invisible here BY CONSTRUCTION: the user's app is visibly broken and their
   * feedback report comes back clean. That is a hole in this module's stated
   * property — every failure reaches the log — not a new feature.
   *
   * ── ⚠ IT IS NOT FILTERED, AND THAT WAS A DELIBERATE REVERSAL ──
   *
   * This card carried an ordering rule: do not land until Cloudflare's
   * JavaScript Detections stops injecting an inline script our CSP blocks on
   * every page load, because the log would "fill with one identical entry per
   * visit and bury everything real".
   *
   * 🔴 **D243 had already refused that change** — no hash can match the inline
   * payload (it carries a per-request token), the only way in is
   * `'unsafe-inline'`, and JS Detections is zone-level so disabling it would
   * weaken beta and the marketing site. So the rule made this card wait for
   * something that was never going to happen.
   *
   * ▶ And the fear does not survive measurement. The buffer lives for ONE page
   * load; Cloudflare's violations are therefore the FIRST entries of a session,
   * and `recentErrors()` sends the LAST ten. Any real activity evicts them
   * before a feedback report is built. They cannot bury anything.
   *
   * ⚠ **Filtering them out was considered and is not possible anyway.** An
   * inline violation reports `blockedURI: 'inline'` with an empty `sample` —
   * measured on the live page — so it cannot be told apart from any other
   * inline block. A filter would have to silence every inline violation,
   * including the one that matters most: a script we did not ship.
   *
   * ⚠ **Whether the Cloudflare ones reach this listener at all is UNKNOWN.**
   * The blocked script is built inside an iframe, and this bundle is a deferred
   * module — so the violation may fire on another document, or before this runs.
   * Not resolved, because it does not change the answer: two evictable entries,
   * or none.
   */
  const onCspViolation = (e: SecurityPolicyViolationEvent): void => {
    try {
      record({
        kind: 'error',
        // ⚠ Names the DIRECTIVE as well as the blocked URI. "Blocked
        // https://fonts.example/x.woff2" does not say which line of the policy
        // to look at, and that line is the fix.
        msg: `Blocked by our own CSP (${e.effectiveDirective || e.violatedDirective}): `
          + `${e.blockedURI || 'inline'}`,
        stack: null,
        // ⚠ Same shape as `onError`'s. Empty for an inline violation, which is
        // why the message carries the directive.
        source: e.sourceFile ? `${e.sourceFile}:${e.lineNumber}:${e.columnNumber}` : null,
      });
    } catch { /* swallowed */ }
  };

  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  target.addEventListener('securitypolicyviolation', onCspViolation);

  return () => {
    target.console.error = origError;
    target.console.warn = origWarn;
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    target.removeEventListener('securitypolicyviolation', onCspViolation);
    installed = false;
  };
}
